import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatGBP, formatUKDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Bell, ExternalLink, Copy } from "lucide-react";

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) => filter === "all" ? true : filter === "paid" ? i.status === "paid" : i.status !== "paid");

  const remind = async (id) => { await api.post(`/invoices/${id}/send-reminder`); toast.success("Reminder sent"); };
  const copyLink = (token) => {
    const url = `${window.location.origin}/pay/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Payment link copied");
  };

  const outstanding = items.filter((i) => i.status !== "paid").reduce((a, b) => a + b.amount, 0);
  const paidTotal = items.filter((i) => i.status === "paid").reduce((a, b) => a + b.amount, 0);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl tracking-tight font-bold">Invoices</h1>
        <div className="flex gap-2">
          {["all", "unpaid", "paid"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`rounded-md px-3 py-1.5 text-sm ${filter === f ? "bg-primary text-primary-foreground" : "border border-border hover:bg-muted"}`} data-testid={`invoice-filter-${f}`}>
              {f[0].toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Outstanding</div>
          <div className="mt-2 font-display text-3xl font-bold text-amber-700">{formatGBP(outstanding)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Collected (all time)</div>
          <div className="mt-2 font-display text-3xl font-bold text-emerald-700">{formatGBP(paidTotal)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Invoices</div>
          <div className="mt-2 font-display text-3xl font-bold">{items.length}</div>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent text-left text-xs uppercase tracking-[0.14em] font-bold text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Issued</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr> :
               filtered.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No invoices.</td></tr> :
               filtered.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{formatUKDate(inv.issued_date)}</td>
                  <td className="px-4 py-3"><div className="font-medium">{inv.customer_name}</div><div className="text-xs text-muted-foreground">{inv.customer_email}</div></td>
                  <td className="px-4 py-3 font-semibold">{formatGBP(inv.amount)}</td>
                  <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-3 text-xs capitalize">{inv.payment_method || "—"}</td>
                  <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                    <a href={`/pay/${inv.token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs rounded-md border border-border px-2 py-1 hover:bg-muted" data-testid={`open-pay-${inv.id}`}><ExternalLink className="h-3 w-3" /> Open</a>
                    <button onClick={() => copyLink(inv.token)} className="inline-flex items-center gap-1 text-xs rounded-md border border-border px-2 py-1 hover:bg-muted" data-testid={`copy-link-${inv.id}`}><Copy className="h-3 w-3" /> Copy link</button>
                    {inv.status !== "paid" && (
                      <button onClick={() => remind(inv.id)} className="inline-flex items-center gap-1 text-xs rounded-md border border-border px-2 py-1 hover:bg-muted" data-testid={`remind-${inv.id}`}><Bell className="h-3 w-3" /> Remind</button>
                    )}
                  </td>
                </tr>
               ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
